import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Shield, CheckCircle2, Loader2, AlertCircle, Building2, XCircle } from "lucide-react";
import api from "../lib/api";

import { useAppConfig } from "../lib/AppConfigContext";

export default function AdminScrutinyPage() {
  const { t } = useTranslation();
  const { appName } = useAppConfig();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [rsaRequired, setRsaRequired] = useState(false);
  const [rsaPrivateKey, setRsaPrivateKey] = useState("");

  useEffect(() => {
    loadRequestInfo();
  }, [token]);

  const loadRequestInfo = async () => {
    try {
      const response = await api.get(`/scrutiny/request/${token}`);
      setRequestInfo(response.data);
      setRsaRequired(response.data.rsa_required || false);
      
      if (response.data.status !== 'pending') {
        if (response.data.status === 'admin_approved') {
          toast.info(t('adminScrutiny.alreadyApproved'));
        } else if (response.data.status === 'completed') {
          toast.info(t('scrutiny.alreadyCompleted'));
        }
      }
    } catch (error) {
      toast.error(t('scrutiny.requestNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rsaRequired && !rsaPrivateKey.trim()) {
      toast.error(t('adminConsultes.rsaDecryptError'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/scrutiny/admin-approve", {
        token,
        admin_rsa_private_key: rsaRequired ? rsaPrivateKey.trim() : ""
      });
      
      setCompleted(true);
      toast.success(t('adminScrutiny.approved'));
    } catch (error) {
      const detail = error.response?.data?.detail || t('adminScrutiny.processError');
      setErrorMsg(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!requestInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('scrutiny.requestNotFound')}</h1>
          <p className="text-slate-500">{t('scrutiny.requestNotFoundDesc')}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('adminScrutiny.approvedTitle')}</h1>
          <p className="text-slate-500 mb-6">
            {t('adminScrutiny.emailSentToEntity')}
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-left">
            <p className="text-sm text-slate-600">
              <strong>{t('common.entity')}:</strong> {requestInfo.entity_name}
            </p>
            <p className="text-sm text-slate-600">
              <strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/consultes')}
            className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
            data-testid="back-to-admin-btn"
          >
            {t('adminScrutiny.backToAdmin', 'Tornar')}
          </Button>
        </motion.div>
      </div>
    );
  }

  if (requestInfo.status !== 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('adminScrutiny.alreadyProcessedTitle')}</h1>
          <p className="text-slate-500 mb-4">
            {requestInfo.status === 'admin_approved' 
              ? t('adminScrutiny.alreadyApprovedDesc')
              : t('adminScrutiny.alreadyCompletedDesc')}
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-left">
            <p className="text-sm text-slate-600">
              <strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}
            </p>
            <p className="text-sm text-slate-600">
              <strong>{t('common.status')}:</strong> {requestInfo.status}
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/consultes')}
            className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
            data-testid="back-to-admin-btn"
          >
            {t('adminScrutiny.backToAdmin', 'Tornar')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
            <Shield className="h-7 w-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('adminScrutiny.approveScrutiny')}</h1>
            <p className="text-slate-500">{t('adminScrutiny.administration')} {appName}</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{requestInfo.entity_name}</p>
              <p className="text-sm text-blue-700">{t('adminScrutiny.requestsScrutinyFor')}:</p>
              <p className="text-sm font-semibold text-blue-800 mt-1">{requestInfo.consulta_title}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {rsaRequired ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('adminConsultes.rsaPrivateKeyRequired')}
              </label>
              <p className="text-xs text-slate-500 mb-2">{t('adminConsultes.rsaPrivateKeyDesc')}</p>
              <textarea
                value={rsaPrivateKey}
                onChange={(e) => setRsaPrivateKey(e.target.value)}
                rows={6}
                className="w-full border border-slate-300 rounded-lg p-3 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                placeholder={t('adminConsultes.rsaPrivateKeyPlaceholder')}
                data-testid="rsa-private-key-input"
              />
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              {t('adminScrutiny.autoKeyDesc')}
            </p>
          )}

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="approve-scrutiny-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-5 w-5 mr-2" />
            )}
            {t('adminScrutiny.approveAndNotify')}
          </Button>
        </div>
      </motion.div>

      {/* Error Dialog */}
      <Dialog open={!!errorMsg} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              {t('scrutiny.incorrectKey')}
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              {errorMsg}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {t('scrutiny.reviewAndRetry')}
          </p>
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => { setErrorMsg(""); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="error-accept-btn"
            >
              {t('common.accept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
